import { ClipboardEvent, memo } from 'react';

import styles from './SmsCodeInput.module.css';

export interface SmsCodeInputProps {
  code: string[];
  isError: boolean;
  onCodeChange: (code: string[]) => void;
}

export const SmsCodeInput = memo(
  ({ code, isError, onCodeChange }: SmsCodeInputProps) => {
    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
      // Stop data actually being pasted into div
      event.stopPropagation();
      event.preventDefault();

      // Get pasted data via clipboardData
      const clipboardData = event.clipboardData;
      const pastedData = clipboardData.getData('Text');

      // we want to clip the string so that it is no longer than our maximum length and then convert it to an array
      const currentCode = pastedData.substring(0, code.length).split('');
      // get the length of the string as an index so that we can use this to move the cursor to the next box (if available)
      const endIndex = currentCode.length - 1;
      // if the length of the clipboard value is less than our input, then pad the end of the array with empty strings
      if (currentCode.length < code.length) {
        do {
          currentCode.push('');
        } while (currentCode.length < code.length);
      }
      // then move the cursor to the next relevant input box
      const nextInput = document.getElementById(
        `input-${endIndex < code.length - 1 ? endIndex + 1 : endIndex}`,
      );
      nextInput?.focus();
      // set the parent components code
      onCodeChange(currentCode);
    };

    const handleChange = (
      index: number,
      event?: React.ChangeEvent<HTMLInputElement>,
      keyboardValue?: string,
    ) => {
      const value = keyboardValue ?? event?.target.value ?? '';

      const newValues = [...code];
      newValues[index] = value;
      onCodeChange(newValues);

      // check if there is no value but there previously was a value
      const hasDeleted = !value && code[index] !== '';

      // if we haven't deleted, and we aren't on the last input, then move onto the next ref
      if (!hasDeleted && index < code.length) {
        const nextInput = document.getElementById(`input-${index + 1}`);
        nextInput?.focus();
      }
    };

    const handleKeyPress = (
      index: number,
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      // listen for the backspace key being pressed and if we aren't on the first input then move the focused input back
      if (event.key === 'Backspace' && index !== 0 && !code[index]) {
        const previosInput = document.getElementById(`input-${index - 1}`);
        previosInput?.focus();
      } else if (code[index]) {
        if (/[0-9]/.test(event.key) && index < code.length) {
          handleChange(index, undefined, event.key);
        } else {
          const nextInput = document.getElementById(`input-${index + 1}`);
          nextInput?.focus();
        }
      }
    };

    return (
      <div className={styles.smsCode}>
        {code.map((value, index) => (
          <input
            key={index}
            className={`${styles.smsCodeInput} ${
              isError ? styles.errorInput : ''
            }`}
            id={`input-${index}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value}
            onChange={(event) => handleChange(index, event)}
            onKeyUp={(event) => handleKeyPress(index, event)}
            onPaste={handlePaste}
          />
        ))}
      </div>
    );
  },
);
